import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { SlimEmptyView } from "../../../../../Components/Empty";
import { Tile } from "../../../../../Components/Tile";
import { CourseSectionFilterInput, useCourseSectionListQuery } from "../../../../../Utilities";
import { CourseSectionLink } from "../../../../Links";
import { CourseSectionSimpleCard } from "../../Card";
import { ControlledCourseSectionsListModal, ControlledCourseSectionsListModalProps } from "../../ListModal";

export type ControlledCourseSectionsGridTileProps = {
	filters?: CourseSectionFilterInput;
	columns: number;
	rows?: number;
	slim?: boolean;
} & Pick<ControlledCourseSectionsListModalProps, "groupBy"> &
	Partial<ClassNameProps & StyleProps>;

export function ControlledCourseSectionsGridTile({
	filters,
	columns,
	rows = 1,
	slim,
	groupBy,
	className,
	style,
}: ControlledCourseSectionsGridTileProps) {
	const { loading, data } = useCourseSectionListQuery({
		variables: { filters, pagination: { first: columns * rows } },
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const numCourseSections = data?.courseSectionConnection.edges.length ?? 0;

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Heading.H2 noMargin>Courses</Heading.H2>

						<If condition={numCourseSections > 0}>
							<Button variant="link" size="small" onClick={openModal}>
								View All
							</Button>
						</If>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<Conditional>
						<If condition={loading && !data}>Loading...</If>
						<If condition={!!data && numCourseSections === 0}>
							<SlimEmptyView>No courses listed</SlimEmptyView>
						</If>
						<Else>
							<Grid tracks={columns} gap="0.75rem">
								{data?.courseSectionConnection.edges.map(({ node }) => (
									<CourseSectionLink key={node.id} instance={node} className="link--no-text-decoration">
										<CourseSectionSimpleCard courseSection={node} slim={slim} />
									</CourseSectionLink>
								))}
							</Grid>
						</Else>
					</Conditional>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<ControlledCourseSectionsListModal filters={filters} groupBy={groupBy} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
